<script setup lang="ts">
import { FwbDropdown, FwbListGroup } from 'flowbite-vue';
import { computed, onMounted, reactive, ref } from 'vue';
import { useDark, useToggle } from '@vueuse/core';
import { useAuthStore } from '@/stores/auth';
import type { UserInfo } from '@/types/backend-data';
import { useI18n } from 'vue-i18n';
import { useLocaleStore } from '@/stores/locale';
import api from '@/http';
import { useRouter } from 'vue-router';
import useEventsBus from '@/utils/eventbus';

import avatarMan from '@/assets/images/man_avatar.svg';
import avatarWoman from '@/assets/images/woman_avatar.svg';

const { t } = useI18n();
const { gEmit } = useEventsBus();
const isDark = useDark();
const toggleDark = useToggle(isDark);
const router = useRouter();
const localeStore = useLocaleStore();
const userInfoIsLoading = ref(false);
const authStore = useAuthStore();
const userMenu = computed(() => [
  { id: 1, action: 'profile', iconName: 'UserRoundCog', title: 'Profile' },
  { id: 2, action: 'setting', iconName: 'Settings', title: 'Setting' },
  { id: 3, action: 'logout', iconName: 'LogOut', title: 'Log Out' }
]);

const states = reactive({
  onProcess: false
});

const userInfo = computed<UserInfo | null>(() => {
  if (!authStore.userInfo.data) return null;
  return authStore.userInfo.data;
});

function getAvatar(tipe: any): string {
  switch (tipe) {
    case true:
      return avatarMan;
    case false:
      return avatarWoman;
    // case 3:
    // return avatar3;
    default:
      return avatarMan;
  }
}

const doLogout = async () => {
  if (states.onProcess === true) return;

  states.onProcess = true; //limit the request to BE
  authStore.resetAuthData(); //destroy auth user info
  try {
    const response = await api.post(`/auth/logout`);
    if (response && response.data) router.replace({ name: 'authLogin' });
  } catch (error: any) {
    console.error(error);
  } finally {
    states.onProcess = true;
  }
};

const showUnderContruction = () =>
  gEmit('showMessage', 'warning', 'text.warning', 'this feature is under contruction');

const doAction = (actionName: string) => {
  switch (actionName) {
    case 'logout':
      doLogout();
      break;

    default:
      showUnderContruction();
      break;
  }
};

//mengupdate informasi user di pinia state management
//setiap kali browser direfresh
const getMyUserInfo = async () => {
  if (userInfoIsLoading.value === true) return;
  userInfoIsLoading.value = true;
  try {
    const response = await api.get(`/auth/user-info`);
    if (response && response.data) {
      //simpan ke pinia
      authStore.saveUserInfo(response.data);
      userInfoIsLoading.value = false;
    }
  } catch (error: any) {
    // userInfoIsLoading.value = false;
    // if (error && error.response) {
    //   gEmit("showMsgbox", {
    //     title: "error.error",
    //     message: "text.error." + error.response.data.message,
    //     type: "error",
    //   });
    // }
    console.error(error);
  } finally {
    userInfoIsLoading.value = false;
  }
};

onMounted(() => getMyUserInfo());
</script>
<template>
  <div
    class="sticky z-30 top-0 w-full bg-sky-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-8 rounded-none lg:rounded-b-lg border border-t-0 dark:border-gray-700 border-opacity-50 shadow-lg"
  >
    <div class="flex items-center justify-between py-3 lg:py-6">
      <div class="flex justify-start items-start gap-x-6">
        <div class="flex justify-start items-center gap-x-2 flex-shrink-0">
          <img :src="getAvatar(userInfo?.gender)" alt="login" class="relative object-cover h-12 lg:h-32" />
        </div>
        <div
          v-if="userInfoIsLoading"
          class="hidden lg:flex flex-col justify-start items-start text-inherit "
        >
          <span class=" text-inherit animate-pulse">
            <h4>{{ t('home.loading user information') }}...</h4>
          </span>
        </div>
        <div v-else class="hidden lg:flex flex-col justify-start items-start text-inherit ">
          <span class=" text-inherit">
            <h4>{{ t('home.login as') }}</h4>
          </span>
          <div class="mt-1 max-w-xl">
            <p class="font-semibold text-lg">
              {{ userInfo?.nama_tanpa_gelar }}
              <span class=" font-light">{{ userInfo?.username }}</span>
            </p>
            <p class="text-base">{{ userInfo?.nama_jabatan }}</p>
            <p class="text-base">
              {{ `${userInfo?.nama_bagian} - ${userInfo?.nama_unit}` }}
            </p>
            <p class="text-base">
              {{ userInfo?.kode_perusahaan }}
            </p>
          </div>
        </div>
      </div>
      <div class="flex text-base justify-end items-center gap-x-3">
        <button
          @click="toggleDark()"
          class="text-inherit cursor-pointer z-10 hover:text-sky-500"
        >
          <neo-icon name="SunMoon" :stroke-width="2" :size="28" class="left-0 bottom-0" />
        </button>
        <button
          class="text-inherit cursor-pointer z-10 hover:text-sky-500 mr-3"
          @click="localeStore.toggle"
        >
          <neo-icon name="Languages" :stroke-width="2" :size="28" />
        </button>
        <fwb-dropdown align-to-end>
          <template #trigger>
            <button class="text-inherit cursor-pointer z-10 hover:text-sky-500">
              <neo-icon
                name="CircleUserRound"
                :stroke-width="2"
                :size="32"
                class="left-0 bottom-0"
              />
            </button>
          </template>

          <fwb-list-group>
            <p
              class="block lg:hidden p-3  w-full font-normal bg-slate-500 dark:bg-gray-900 text-white"
            >
              <span class="font-semibold">{{ userInfo?.username }}</span>
              <br />
              {{ userInfo?.nama_tanpa_gelar }}
              <br />
              {{ userInfo?.nama_jabatan }}
            </p>
            <ul class="user__menu">
              <li v-for="(item, index) in userMenu" :key="index" @click="doAction(item.action)">
                <neo-icon
                  :name="item.iconName"
                  :stroke-width="2"
                  :size="20"
                  class="text-gray-600 dark:text-gray-300 -top-[10px] left-0 bottom-0"
                />
                {{ item.title }}
              </li>
            </ul>
          </fwb-list-group>
        </fwb-dropdown>
      </div>
    </div>
    <Teleport to="body">
      <div
        v-if="states.onProcess === true"
        class="z-50 fixed flex flex-col justify-center items-center top-0 left-0 h-screen w-screen transition-all bg-opacity-70 bg-black"
      >
        <div
          class="w-48 h-36 flex flex-col justify-center items-center bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-lg shadow-lg"
        >
          <neo-icon name="LoaderCircle" class="h-16 w-16 animate-spin" :stroke-width="2" />
          <div class="uppercase text-lg">{{ t('home.processing') }}...</div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
<style lang="scss" scoped>
.user__menu {
  li {
    @apply text-md p-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 flex justify-start items-center gap-x-2;
  }
}
</style>
